import React, { useEffect, useState, useMemo } from "react";
import { Box, CircularProgress, Button } from "@mui/material";
import api from "../../../api/axios";
import { Link } from "react-router-dom";

import OnSaleTable from "./OnSaleTable";
import OfferDeleteAllModal from "./modals/OfferDeleteAllModal";
import { OfferDTO, GroupedOffer } from "./types";

export type SortField = "price" | "creationDate";
export type SortDir = "asc" | "desc";

const OnSale: React.FC = () => {
  const [offers, setOffers] = useState<OfferDTO[]>([]);
  const [totalElements, setTotalElements] = useState(0);
  const [loading, setLoading] = useState(true);

  const [sortField, setSortField] = useState<SortField>("creationDate");
  const [sortDir, setSortDir] = useState<SortDir>("desc");

  const [page, setPage] = useState(0);
  const [size, setSize] = useState(8);

  const [deleteAllOpen, setDeleteAllOpen] = useState(false);

  useEffect(() => {
    const load = async () => {
      setLoading(true);

      const params: any = {
        page,
        size,
        sort: `${sortField},${sortDir}`,
      };

      const res = await api.get(`/on-sale`, { params });

      setOffers(res.data.content ?? []);
      setTotalElements(res.data.totalElements ?? 0);
      setLoading(false);
    };

    load();
  }, [sortField, sortDir, page, size]);

  const grouped: GroupedOffer[] = useMemo(() => {
    const map = new Map<string, GroupedOffer>();

    offers.forEach((offer) => {
      const key = offer.item.catalog.marketHashName ?? offer.item.itemAssetId;

      if (!map.has(key)) {
        map.set(key, {
          key,
          item: offer.item,
          steamAccount: offer.steamAccount,
          price: offer.priceMinor,
          status: offer.status,
          offerCreationDate: offer.offerCreationDate,
          offers: [offer],
        });
      } else {
        map.get(key)!.offers.push(offer);
      }
    });

    return Array.from(map.values());
  }, [offers]);

  const handleOffersDeleted = (ids: string[]) => {
    setOffers((prev) => prev.filter((o) => !ids.includes(o.id)));
    setTotalElements((prev) => Math.max(prev - ids.length, 0));
  };

  const handleOffersUpdated = (updated: { offerId: string; price: number }[]) => {
    setOffers((prev) =>
        prev.map((o) => {
          const found = updated.find((u) => u.offerId === o.id);
          return found ? { ...o, priceMinor: found.price } : o;
        })
    );
  };

  const handleDeleteAllSuccess = () => {
    setOffers([]);
    setTotalElements(0);
  };

  return (
      <Box className="p-4 mt-20 flex flex-col gap-4 min-h-screen">
        {loading && (
            <div className="w-full flex justify-center items-center flex-1">
              <CircularProgress />
            </div>
        )}

        {!loading && totalElements > 0 && (
            <div className="flex justify-end">
              <Button
                  color="error"
                  variant="contained"
                  onClick={() => setDeleteAllOpen(true)}
              >
                Delete All Offers
              </Button>
            </div>
        )}

        {!loading && totalElements === 0 && (
            <div className="flex flex-col flex-1 justify-center items-center text-center gap-4 text-gray-300">
              <div className="text-2xl font-semibold text-white">
                No items on sale yet
              </div>

              <div className="text-sm text-gray-400 max-w-md">
                You currently don’t have any sale offers.
              </div>

              <Link
                  to="/sell"
                  className="mt-4 inline-flex items-center justify-center rounded-lg bg-blue-600 px-6 py-2 text-white font-semibold hover:bg-blue-500 transition-colors"
              >
                Sell items now
              </Link>
            </div>
        )}

        {!loading && totalElements > 0 && grouped.length > 0 && (
            <OnSaleTable
                groupedOffers={grouped}
                multipleSteamAccounts={false}
                sortField={sortField}
                sortDir={sortDir}
                page={page}
                size={size}
                totalElements={totalElements}
                onPageChange={setPage}
                onPageSizeChange={(s) => {
                  setSize(s);
                  setPage(0);
                }}
                onSortChange={(field, dir) => {
                  setSortField(field);
                  setSortDir(dir);
                  setPage(0);
                }}
                onOffersDeleted={handleOffersDeleted}
                onOffersUpdated={handleOffersUpdated}
            />
        )}

        <OfferDeleteAllModal
            open={deleteAllOpen}
            onClose={() => setDeleteAllOpen(false)}
            totalOffers={totalElements}
            onAllDeleted={handleDeleteAllSuccess}
        />
      </Box>
  );
};

export default OnSale;
